/**
 * Which verb a review gate offers, from what GitHub last said about the pull
 * request (issue #125).
 *
 * The gate used to draw the same row of buttons whatever was true: Open PR,
 * Mark merged, Send back, Reassign, Cancel, for a branch that might have been
 * conflicting for an hour. The rule this file encodes is that the primary verb
 * is never one that would fail if pressed.
 *
 * | PR state              | Primary                       | Also offered      |
 * |-----------------------|-------------------------------|-------------------|
 * | Green and mergeable   | Open PR                       | Mark merged       |
 * | CI red                | Fix the PR                    | Open PR, Cancel   |
 * | Merge conflict        | Rebase and fix                | Open PR, Cancel   |
 * | Checks still running  | disabled, "Checks running, 2 of 6" | Open PR      |
 * | No PR was opened      | the `gh pr create` line (#120) | Cancel           |
 *
 * A **mirror** of `reviewGateState` in `@dyspatch/core`, on the same terms as
 * the other lists in this directory: importing a core *value* into the browser
 * bundle pulls `node:crypto` in with it. `packages/hub/test/review-gate.test.ts`
 * pins the two against each other, so a change to one that is not made to the
 * other fails rather than silently giving the console a different opinion from
 * the hub about what is safe to press.
 */

export type ReviewGateState = 'no_pr' | 'unknown' | 'conflict' | 'red' | 'running' | 'green';

/** What the gate reads off a task or a review-queue row. Every field may be absent. */
export interface ReviewGatePr {
  prUrl?: string | null;
  prCheckState?: string | null;
  prFailingChecks?: readonly string[] | null;
  prChecksTotal?: number | null;
  prChecksCompleted?: number | null;
  prMergeable?: boolean | null;
  prMergeableState?: string | null;
  prStateConfirmedAt?: string | null;
}

/**
 * `dirty` is GitHub's word for "this branch conflicts with its base".
 *
 * Read off `mergeable_state` and not off `mergeable === false`: `mergeable` is
 * also false for a PR merely blocked on a required review, and telling somebody
 * to rebase over a review they have not done yet is the wrong-verb problem in
 * a new costume.
 */
export function isMergeConflict(pr: ReviewGatePr): boolean {
  return pr.prMergeableState === 'dirty';
}

export function reviewGateState(pr: ReviewGatePr | null | undefined): ReviewGateState {
  if (!pr?.prUrl) return 'no_pr';
  // Nothing has ever been confirmed: a task that reached review before the
  // watch existed, or a repo with no webhook and no token. The gate keeps the
  // shape it always had rather than inventing a verdict it has not earned.
  if (!pr.prStateConfirmedAt) return 'unknown';
  if (isMergeConflict(pr)) return 'conflict';
  // After the conflict branch: a conflict is known even when the checks read
  // failed, and `Rebase and fix` is still the right verb for it.
  if (pr.prCheckState === 'unknown') return 'unknown';
  if (pr.prCheckState === 'failing') return 'red';
  if (pr.prCheckState === 'pending') return 'running';
  return 'green';
}

/** "Checks running, 2 of 6", or null when the counts are not both known. */
export function checksRunningLabel(pr: ReviewGatePr): string | null {
  const total = pr.prChecksTotal;
  const done = pr.prChecksCompleted;
  if (total === null || total === undefined || done === null || done === undefined) return null;
  return `Checks running, ${done} of ${total}`;
}

/** The failing checks as one clause: "lint, typecheck and test". */
export function describeFailingChecks(names: readonly string[] | null | undefined): string {
  const list = names ?? [];
  if (list.length === 0) return 'an unnamed check';
  if (list.length === 1) return list[0]!;
  return `${list.slice(0, -1).join(', ')} and ${list.at(-1)}`;
}

export type ReviewGateVerb =
  | 'openPr'
  | 'markMerged'
  | 'fixPr'
  | 'rebaseAndFix'
  | 'checksRunning'
  | 'ghPrCreate'
  | 'cancel';

export interface ReviewGateVerbs {
  state: ReviewGateState;
  /** The one filled button. `checksRunning` and `ghPrCreate` are not pressable. */
  primary: ReviewGateVerb;
  /** The alternatives, in the order they are drawn. */
  also: ReviewGateVerb[];
}

/**
 * The verb set for a state.
 *
 * `markMerged` is deliberately absent from red and conflicted gates. Keeping it
 * there would be offering, as an equal alternative, the one action that writes
 * a permanent record about something GitHub says cannot happen - and the hub's
 * north-star metric counts those records. It is still reachable: the gate's
 * refusal names it, and the hub's own re-confirm lets a person force it past a
 * disagreement they know about (`/api/tasks/:id/done`).
 *
 * Send back and Reassign are not in this table. They are decisions about the
 * *work*, not about the pull request, and a person may want either whatever
 * GitHub thinks; the gate keeps drawing them underneath.
 */
export function reviewGateVerbs(state: ReviewGateState): ReviewGateVerbs {
  switch (state) {
    case 'green':
      return { state, primary: 'openPr', also: ['markMerged'] };
    case 'red':
      return { state, primary: 'fixPr', also: ['openPr', 'cancel'] };
    case 'conflict':
      return { state, primary: 'rebaseAndFix', also: ['openPr', 'cancel'] };
    case 'running':
      return { state, primary: 'checksRunning', also: ['openPr'] };
    case 'no_pr':
      return { state, primary: 'ghPrCreate', also: ['cancel'] };
    // The pre-watch shape, unchanged: this is what every gate looked like
    // before any of this existed, and it is the honest answer for a repo the
    // hub cannot ask GitHub about.
    default:
      return { state, primary: 'openPr', also: ['markMerged'] };
  }
}

/**
 * The sentence under the buttons that says how old this answer is.
 *
 * In the same quiet register as the rest of the task page's timings, and
 * present in every state including the ones nobody has confirmed - "not
 * confirmed yet" is exactly the thing a person could not tell apart from a
 * live answer before, which is why this issue exists.
 */
export function confirmedSentence(
  pr: ReviewGatePr | null | undefined,
  /** `ago(minutesSince(confirmedAt))` from `lib/format`, passed in so this stays pure and testable. */
  agoPhrase: string | null,
): string {
  if (!pr?.prUrl) return '';
  if (!pr.prStateConfirmedAt || !agoPhrase) {
    return 'GitHub has not been asked about this pull request yet.';
  }
  /**
   * Asked, answered about the merge, refused about the checks. Saying only
   * "Checked with GitHub 2m ago" over the pre-watch buttons would read as a
   * live verdict, which is the confusion this whole gate exists to remove.
   */
  if (pr.prCheckState === 'unknown') {
    return `Checked with GitHub ${agoPhrase}, but its checks could not be read.`;
  }
  return `Checked with GitHub ${agoPhrase}.`;
}
